"use client";

import { Target } from "lucide-react";
import type { Ref } from "react";
import { NoiseOverlay } from "@/components/custom/noise_overlay";
import { PRICING_TABS } from "@/components/pricing/pricing_tabs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Shimmer({
	className,
	tone = "light",
}: {
	className?: string;
	tone?: "light" | "dark";
}) {
	const fillClass = tone === "dark" ? "bg-zinc-700/50" : "bg-zinc-200/45";

	return (
		<div
			className={`${className ?? ""} relative overflow-visible rounded-[2px]`}
			data-testid="pricing-skeleton-shimmer"
		>
			<div
				className={`absolute inset-0 scale-x-[1.08] scale-y-[1.28] rounded-[inherit] ${fillClass} blur-[5px]`}
				style={{
					backgroundImage:
						"linear-gradient(90deg, rgba(228,228,231,0.16), rgba(255,255,255,0.5), rgba(228,228,231,0.16))",
					backgroundSize: "200% 100%",
					animation: "shimmer 1.5s infinite",
				}}
			/>
		</div>
	);
}

const STANDARD_PRICING_CARD_HEIGHT = "h-[370px]";
const EDITING_CARD_HEIGHTS = [
	"h-[478px] md:h-[514px] lg:h-[550px]",
	"h-[514px] lg:h-[550px]",
	"h-[550px]",
] as const;
const ENGAGEMENT_CARD_HEIGHT =
	"h-[370px] [@media_(min-width:1024px)_and_(max-width:1199px)]:h-[416px] [@media_(min-width:1200px)_and_(max-width:1399px)]:h-[385px]";
const AUDIT_CARD_HEIGHTS = ["h-[478px] md:h-[550px]", "h-[550px]"] as const;

function PricingCardSkeleton({
	heightClass = STANDARD_PRICING_CARD_HEIGHT,
	large = false,
}: {
	heightClass?: string;
	large?: boolean;
}) {
	return (
		<div
			className={`${heightClass} bg-white rounded-3xl border border-zinc-100 shadow-xl p-8 flex flex-col`}
			data-testid="pricing-card-skeleton"
		>
			<div className="mb-8">
				<Shimmer className="h-5 w-20 mb-2" />
				<Shimmer className="h-10 w-28" />
				<Shimmer className="h-3 w-32 mt-2" />
			</div>
			<div className="flex-1 space-y-4 mb-8">
				<div className="flex items-start gap-3">
					<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
					<Shimmer className="h-4 w-full" />
				</div>
				<div className="flex items-start gap-3">
					<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
					<Shimmer className="h-4 w-5/6" />
				</div>
				<div className="flex items-start gap-3">
					<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
					<Shimmer className="h-4 w-4/6" />
				</div>
				<div className="flex items-start gap-3">
					<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
					<Shimmer className="h-4 w-full" />
				</div>
				{large && (
					<>
						<div className="flex items-start gap-3">
							<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
							<Shimmer className="h-4 w-3/4" />
						</div>
						<div className="flex items-start gap-3">
							<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
							<Shimmer className="h-4 w-5/6" />
						</div>
						<div className="flex items-start gap-3">
							<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
							<Shimmer className="h-4 w-2/3" />
						</div>
					</>
				)}
			</div>
			<Shimmer className="h-12 w-full" />
		</div>
	);
}

function AuditCardSkeleton({
	featureCount = 8,
	heightClass,
}: {
	featureCount?: number;
	heightClass: string;
}) {
	return (
		<div
			className={`${heightClass} bg-white rounded-3xl border border-zinc-100 shadow-xl p-8 flex flex-col`}
			data-testid="audit-card-skeleton"
		>
			{/* Badge */}
			<Shimmer className="h-6 w-24 mb-4" />
			{/* Title & Price */}
			<div className="mb-6">
				<Shimmer className="h-8 w-32 mb-2" />
				<div className="flex items-baseline gap-2">
					<Shimmer className="h-12 w-24" />
					<Shimmer className="h-4 w-16" />
				</div>
				<Shimmer className="h-3 w-28 mt-2" />
			</div>
			{/* Features - more items for audit */}
			<div className="flex-1 space-y-4 mb-8">
				{Array.from({ length: featureCount }, (_, index) => index + 1).map(
					(i) => (
						<div key={i} className="flex items-start gap-3">
							<Shimmer className="w-6 h-1.5 mt-2 shrink-0" />
							<Shimmer
								className={`h-4 ${i % 3 === 0 ? "w-4/6" : i % 2 === 0 ? "w-5/6" : "w-full"}`}
							/>
						</div>
					),
				)}
			</div>
			<Shimmer className="h-12 w-full" />
		</div>
	);
}

const faqItems = [
	{ id: "faq-1", width: "w-64" },
	{ id: "faq-2", width: "w-72" },
	{ id: "faq-3", width: "w-64" },
	{ id: "faq-4", width: "w-72" },
	{ id: "faq-5", width: "w-64" },
];

function FAQSkeleton({ count = 4 }: { count?: number }) {
	const items = faqItems.slice(0, count);
	return (
		<div className="max-w-3xl mx-auto space-y-3">
			{items.map((item) => (
				<div
					key={item.id}
					className="border border-zinc-200 rounded-2xl px-6 py-5 bg-white/50"
				>
					<div className="flex items-center justify-between">
						<Shimmer className={`h-5 ${item.width}`} />
						<Shimmer className="w-5 h-1.5" />
					</div>
				</div>
			))}
		</div>
	);
}

function StaticTabIntro({ children }: { children: string }) {
	return (
		<div className="w-full max-w-3xl mx-auto text-center mb-10">
			<p className="font-inter text-zinc-500 text-base leading-relaxed">
				{children}
			</p>
		</div>
	);
}

function RequirementNote({
	children,
	tone = "amber",
}: {
	children: string;
	tone?: "amber" | "emerald";
}) {
	const dotClass = tone === "emerald" ? "bg-emerald-400" : "bg-amber-400";

	return (
		<div className="w-full max-w-7xl mx-auto mb-8 mt-4">
			<div className="flex items-center justify-center gap-2 text-sm text-zinc-500 font-inter">
				<span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
				<span>{children}</span>
			</div>
		</div>
	);
}

function FAQSkeletonSection({
	title,
	count = 4,
}: {
	title: string;
	count?: number;
}) {
	return (
		<div className="mt-16 text-center">
			<div className="text-center mb-12">
				<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
					{title}
				</h2>
				<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
			</div>
			<FAQSkeleton count={count} />
		</div>
	);
}

function SixCardGrid() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
			{[1, 2, 3, 4, 5, 6].map((i) => (
				<PricingCardSkeleton key={i} />
			))}
		</div>
	);
}

function SingleEngagementCardGrid() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 max-w-3xl mx-auto">
			<PricingCardSkeleton heightClass={ENGAGEMENT_CARD_HEIGHT} />
		</div>
	);
}

function ThreeCardGrid() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
			{EDITING_CARD_HEIGHTS.map((heightClass) => (
				<PricingCardSkeleton
					key={heightClass}
					heightClass={heightClass}
					large
				/>
			))}
		</div>
	);
}

function TwoCardGrid() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
			<AuditCardSkeleton featureCount={6} heightClass={AUDIT_CARD_HEIGHTS[0]} />
			<AuditCardSkeleton heightClass={AUDIT_CARD_HEIGHTS[1]} />
		</div>
	);
}

interface PricingSectionShellProps {
	activeTab?: string;
	sectionId?: string;
	onTabChange?: (value: string) => void;
	sectionRef?: Ref<HTMLElement>;
}

export function PricingSectionShell({
	activeTab = "reach",
	sectionId = "pricing",
	onTabChange,
	sectionRef,
}: PricingSectionShellProps) {
	return (
		<section
			ref={sectionRef}
			id={sectionId}
			className="scroll-mt-20 isolate bg-white py-20 text-zinc-900 font-sans w-full relative overflow-hidden selection:bg-black selection:text-white md:py-24"
			data-testid="pricing-section"
		>
			<NoiseOverlay />
			<div className="absolute inset-0 z-0 pointer-events-none">
				<div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-zinc-50 to-transparent opacity-60 rounded-full blur-3xl" />
			</div>

			<div className="relative z-10 w-full max-w-[1600px] mx-auto flex flex-col">
				<div className="text-center mb-12 px-4">
					<div
						className="font-syne font-bold text-4xl md:text-6xl uppercase tracking-tighter leading-none mb-6 text-zinc-950"
						aria-hidden="true"
					>
						Pricing
					</div>
					<p className="font-inter text-zinc-500 text-sm md:text-base max-w-xl mx-auto leading-relaxed font-medium">
						Transparent investments for exponential digital growth.
					</p>
					<div className="mt-4 min-h-9" aria-hidden="true" />
				</div>

				<Tabs
					value={activeTab}
					className="w-full flex flex-col items-center"
					onValueChange={onTabChange}
				>
					<div className="w-full flex justify-center mb-8 md:mb-8 relative z-40 px-4 md:px-0">
						<TabsList className="bg-zinc-100/80 backdrop-blur-md border border-zinc-200/50 shadow-inner grid grid-cols-2 w-full gap-2 rounded-3xl p-2 h-auto md:inline-flex md:w-auto md:gap-1 md:rounded-full! md:p-1.5">
							{PRICING_TABS.map((tab) => (
								<TabsTrigger
									key={tab.value}
									value={tab.value}
									data-pricing-tab-value={tab.value}
									className={`
                    relative font-inter font-semibold uppercase tracking-wide
                    text-zinc-900 hover:text-zinc-950 transition-[color,background-color,box-shadow] duration-300
                    data-[state=active]:text-red-600 data-[state=active]:shadow-sm
                    data-[state=active]:bg-white
                    flex items-center gap-2
                    w-full justify-center px-2 py-3 text-xs rounded-xl
                  md:w-auto md:px-6 md:py-2.5 md:rounded-full md:text-sm
                  `}
								>
									<span
										className="w-4 h-4 shrink-0 flex items-center justify-center"
										data-pricing-icon="true"
										data-pricing-icon-key={tab.iconKey}
									>
										{tab.renderIcon("text-red-600")}
									</span>
									{tab.label}
								</TabsTrigger>
							))}
						</TabsList>
					</div>

					<div className="w-full px-4 md:px-8 min-h-[50vh] relative">
						{/* Reach/Growth/Engagement: 6 cards (for tabs with many pricing tiers) */}
						<TabsContent value="reach">
							<ViewsTabSkeleton />
						</TabsContent>

						<TabsContent value="growth">
							<SubscribersTabSkeleton />
						</TabsContent>

						<TabsContent value="engagement">
							<MonetizationTabSkeleton />
						</TabsContent>

						{/* Editing: 3 larger cards (fewer but more detailed packages) */}
						<TabsContent value="editing">
							<EditorTabSkeleton />
						</TabsContent>

						{/* Audit: 2 cards with hero header (specialized service) */}
						<TabsContent value="audit">
							<AuditTabSkeleton />
						</TabsContent>

						{/* Targeted: No cards, just header + icons + CTA (custom pricing) */}
						<TabsContent value="targeted">
							<TargetedTabSkeleton />
						</TabsContent>
					</div>
				</Tabs>
			</div>
		</section>
	);
}

function ViewsTabSkeleton() {
	return (
		<section className="w-full mb-12">
			<StaticTabIntro>
				Run targeted Google Video Ads to get your content in front of real
				viewers in your niche.
			</StaticTabIntro>
			<SixCardGrid />
			<RequirementNote>
				Video must be under 5 minutes, as longer videos may result in higher
				campaign costs.
			</RequirementNote>
			<FAQSkeletonSection title="Common Inquiries" />
		</section>
	);
}

function SubscribersTabSkeleton() {
	return (
		<section className="w-full mb-12">
			<StaticTabIntro>
				Audience growth campaigns that bring real, interested viewers to your
				channel through targeted ads.
			</StaticTabIntro>
			<SixCardGrid />
			<FAQSkeletonSection title="Growth Campaign FAQ" />
		</section>
	);
}

function MonetizationTabSkeleton() {
	return (
		<section className="w-full mb-12">
			<StaticTabIntro>
				Long-form engagement campaigns designed to drive deep, sustained viewing
				of your content.
			</StaticTabIntro>
			<SingleEngagementCardGrid />
			<RequirementNote tone="emerald">
				Requires at least one video with 60+ minutes of content
			</RequirementNote>
			<FAQSkeletonSection title="Campaign Questions" count={3} />
		</section>
	);
}

function EditorTabSkeleton() {
	return (
		<section className="w-full mb-12">
			<ThreeCardGrid />
			<FAQSkeletonSection title="Editing FAQ" />
		</section>
	);
}

function AuditTabSkeleton() {
	return (
		<section className="w-full mb-12">
			<TwoCardGrid />
			<FAQSkeletonSection title="Audit FAQ" />
		</section>
	);
}

function TargetedTabSkeleton() {
	return (
		<section className="w-full mb-12">
			<div className="text-center mb-12 max-w-3xl mx-auto">
				<div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full mb-6">
					<Target className="w-4 h-4" />
					<span className="font-inter font-semibold text-sm uppercase tracking-wide">
						Premium Service
					</span>
				</div>
				<h2 className="font-syne font-black text-3xl md:text-4xl uppercase tracking-tight text-zinc-900 mb-4">
					Targeted Views
				</h2>
				<p className="font-inter text-zinc-500 text-base md:text-lg leading-relaxed">
					Premium video advertising through Google Ads targeting your niche
					audience.
				</p>
			</div>

			<div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
				{[1, 2, 3, 4].map((i) => (
					<div key={i} className="text-center">
						<Shimmer className="w-12 h-6 mx-auto mb-4" />
						<Shimmer className="h-5 w-28 mx-auto mb-2" />
						<Shimmer className="h-3 w-36 mx-auto" />
						<Shimmer className="h-3 w-32 mx-auto mt-1" />
					</div>
				))}
			</div>

			<div className="bg-zinc-900 rounded-3xl p-8 md:p-12 max-w-4xl mx-auto mb-16">
				<div className="text-center">
					<Shimmer className="h-8 w-80 mx-auto mb-4" tone="dark" />
					<Shimmer className="h-4 w-96 mx-auto mb-2" tone="dark" />
					<Shimmer className="h-4 w-64 mx-auto mb-6" tone="dark" />
					<Shimmer className="h-11 w-48 mx-auto" tone="dark" />
				</div>
			</div>

			<FAQSkeletonSection title="Targeted Views FAQ" count={5} />
		</section>
	);
}
